import { useQuery } from 'react-query'
import { useAtomValue } from 'jotai'
import { strideAccountAtom } from '@/atoms/Wallet'
import { queryKeys } from '@/query-keys'
import { fatal } from '@/utils'
import { convertDenomToMicroDenom } from '@/atoms/Wallet'
import { redis } from '../redis'

type RedisRebateStatus = 'UNCLAIMED' | 'PENDING' | 'CLAIMED'

type RebateStatus = 'ineligible' | 'unclaimed' | 'pending' | 'claimed'

export interface RebateProgramValue {
  status: RebateStatus
  amount: number
}

const useRebateProgramQuery = () => {
  const strideAccount = useAtomValue(strideAccountAtom)

  const handleRequest = async (): Promise<RebateProgramValue> => {
    if (!strideAccount) {
      throw fatal('Unable to request rebate program data while wallet is disconnected.')
    }

    const status = await redis.get<RedisRebateStatus>(`status_${strideAccount.key.bech32Address}`)

    if (!isRedisRebateStatus(status)) {
      return { status: 'ineligible', amount: 0 }
    }

    const reward = await redis.get<number>(`rebate_${strideAccount.key.bech32Address}`)

    const rebateStatus: Record<RedisRebateStatus, RebateStatus> = {
      UNCLAIMED: 'unclaimed',
      PENDING: 'pending',
      CLAIMED: 'claimed'
    }

    return {
      status: rebateStatus[status],
      // We'll convert it into a micro denom to make it consistent
      // with the values we get from the RPC for the other queries.
      amount: reward ? convertDenomToMicroDenom(reward) : 0
    }
  }

  return useQuery(
    queryKeys.airdropRebateProgramByAddress({ address: strideAccount?.key.bech32Address ?? '' }),
    handleRequest,
    { enabled: Boolean(strideAccount) }
  )
}

const isRedisRebateStatus = (value: any): value is RedisRebateStatus => {
  return ['UNCLAIMED', 'PENDING', 'CLAIMED'].includes(value)
}

export { useRebateProgramQuery }
